import _ from 'lodash';
import React from 'react';
import { Redirect, Route } from 'react-router-dom';
import * as Sentry from '@sentry/react';

// Data
import { getAuthUser } from '../../../lib/services/auth.service';
import { APP_TITLE, HOME_ROUTE, ROUTE_ROLES } from '../../../../_configuration';
import { staffLoginPathWithNext } from '../../../lib/helpers/next-path.helpers';

// Components
import ErrorPage from '../../_pages/_boilerplate/ErrorPage/error-page.container';
import NoCompanyPage from '../../_pages/_boilerplate/NoCompanyPage/no-company-page.component';
import { getCurrMember } from '../../../lib/services/member.service';
import LoadingPage from '../../_pages/_boilerplate/LoadingPage/loading-page.container';
import { getCompany } from '../../../lib/services/company.service';
import { setCompanyHasAutomation, setCompanyHasCalibrationVideos, setCompanyHasReportCards, setCompanyLogo, setCompanySport } from '../../../lib/stores/company.store';
import { Sport } from '../../../lib/types';
import { providedMemberHasRoleInAnyScope } from '../../../lib/services/user.service';
import { getAssessments } from '../../../lib/services/assessment.service';

interface RouteByRoleProps {
  componentsByRole?: any;
  component?: any;
  title?: string;
  exact?: boolean;
  path: string;
  activeTab?: string;
  wrapper?: any;
  chat?: boolean;
}

export const RouteByRole: React.FC<RouteByRoleProps> = ({ chat, componentsByRole, component, title, ...rest })  => {
  const user = getAuthUser();
  const { member, loading } = getCurrMember();
  const { company } = getCompany(_.get(user, 'settings.activeCompanyId'));
  const {assessments} = getAssessments()

  if (company) {
    setCompanyHasAutomation(company.hasAutomation ?? false);
    setCompanyHasReportCards(company.hasReportCards ?? false);
    setCompanyHasCalibrationVideos(company.hasCalibrationVideos ?? false);
    setCompanySport((company.sport as Sport) ?? Sport.default);
    if (company.logo) setCompanyLogo(company.logo);
  }

  let Component: any;
  if (componentsByRole && user) {
    if (!member && loading) {
      Component = LoadingPage;
    } else if (!_.get(user, 'settings.activeCompanyId')) {
      Component = NoCompanyPage;
    } else if (member) {
      const roleKeys = Object.keys(componentsByRole);
      for (let i = 0; i < roleKeys.length && !Component; i += 1) {
        if (providedMemberHasRoleInAnyScope({member, assessments, roles: [roleKeys[i]]})) {
          Component = componentsByRole[roleKeys[i]];
        }
      }

      if (!Component) {
        Component = componentsByRole[ROUTE_ROLES.AUTHORIZED];
      }
    }
  }

  Component = Component || component;

  // A PUBLIC route is viewable by anyone, so honor its fallback BEFORE the
  // authenticated-but-unmatched HOME_ROUTE redirect (else a logged-in user is bounced).
  Component = Component || componentsByRole?.[ROUTE_ROLES.PUBLIC];

  // Staff-tree only: a parent-only session (no `user`) falls through to LOGIN_ROUTE.
  // See CLAUDE.md → "Parent-only sessions never bounce from staff tree into /parent".
  if (!Component && user)
    return <Route render={() => <Redirect to={HOME_ROUTE} />} />;

  // The attempted path rides along so an emailed deep link survives the login bounce.
  if (!Component)
    return <Route render={props => <Redirect to={staffLoginPathWithNext(props.location.pathname, props.location.search)} />} />;

  document.title = title || APP_TITLE;

  return (
    <Route
      {...rest}
      render={props => (
        <Sentry.ErrorBoundary fallback={ErrorPage}>
          <Component {...props} />
        </Sentry.ErrorBoundary>
      )}
    />
  );
};
